import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { getEmojiMaskUrl, getShapedAvatarBorderStyle } from '@/lib/emojiMask';

interface DittoAvatarProps {
  src?: string;
  alt?: string;
  shape?: string;
  /** Size in pixels. Defaults to 40. */
  size?: number;
  className?: string;
  /** Whether to show the drop-shadow border for shaped avatars. */
  bordered?: boolean;
  /** Custom border color for the drop-shadow. */
  borderColor?: string;
}

export function DittoAvatar({
  src,
  alt = 'Avatar',
  shape,
  size = 40,
  className,
  bordered = false,
  borderColor,
}: DittoAvatarProps) {
  const maskUrl = useMemo(() => (shape ? getEmojiMaskUrl(shape) : ''), [shape]);

  const maskStyle: React.CSSProperties | undefined = maskUrl
    ? {
        WebkitMaskImage: `url(${maskUrl})`,
        maskImage: `url(${maskUrl})`,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
      }
    : undefined;

  const borderStyle = bordered && maskUrl ? getShapedAvatarBorderStyle(borderColor) : undefined;

  const fallbackInitial = alt ? alt[0]?.toUpperCase() : '?';

  const avatar = (
    <div
      className={cn(
        'relative flex shrink-0 overflow-hidden bg-muted',
        !maskUrl && 'rounded-full',
        className,
      )}
      style={{
        width: size,
        height: size,
        ...maskStyle,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-primary/20 text-primary font-semibold text-sm">
          {fallbackInitial}
        </div>
      )}
    </div>
  );

  if (borderStyle) {
    return <div style={borderStyle}>{avatar}</div>;
  }

  return avatar;
}
